"use client";
import { usePathname, useRouter } from "next/navigation";
import type { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime";
import {
  type PathParams,
  serializePathParams,
} from "@khol/utils/network/serializePathParams";
import {  type QueryParams, serializeQueryParams,} from "@khol/utils/network/serializeQueryParams";

export function useNavigation<T extends string>(locale?: string) {
  const router = useRouter();
  const pathname = usePathname();

  function willNavigate<C>(navigate: (path: string, config?: C) => void) {
    return function <P extends T>(
      path: P,
      options?: {
        queryParams?: QueryParams;
        pathParams?: PathParams<P>;
        config?: C;
      }
    ) {
      const qParams = serializeQueryParams(options?.queryParams || {});
      let pathMapped = "";
      if (locale) {
        pathMapped = "/" + locale + "/";
      }
      pathMapped += options?.pathParams
        ? serializePathParams(path, options?.pathParams)
        : path;
      if (qParams) {
        pathMapped += `?${qParams}`;
      }
      navigate(pathMapped, options?.config);
    };
  }

  const push = willNavigate<NavigateOptions>((path) => router.push(path));
  const replace = willNavigate<NavigateOptions>((path) =>
    router.replace(path, {})
  );
  const switchLanguage = (lang: string) => {
    const path = pathname.replace(`/${locale}`, `/${lang}`);
    router.replace(path, {});
  };
  const shallowPush = willNavigate((path) =>
    window.history.pushState({}, "", path)
  );

  function pathWithoutLocale() {
    return pathname.replace(`/${locale}`, "");
  }

  return {
    ...router,
    push,
    shallowPush,
    replace,
    switchLanguage,
    pathWithoutLocale,
  };
}
